import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEO = ({ 
  title = "Debatefy - Learn Debating with AI & Real-time Fact-Checking",
  description = "Everything about debates: definitions, techniques, examples and tips for fair argumentation. Learn to structure discussions and present convincingly. Check arguments, correct errors, think clearer.",
  keywords = "learn debating, AI debate, practice argumentation, fact-checking, discussion vs debate, debate techniques",
  image = "https://debatefy.online/og-image.jpg",
  url = "https://debatefy.online/",
  type = "website"
}) => {
  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      
      {/* Canonical */}
      <link rel="canonical" href={url} />
    </Helmet>
  );
};

export default SEO;

// Pre-configured SEO for different pages
export const SEOConfigs = {
  home: {
    title: "Debatefy - Learn Debating with AI & Real-time Fact-Checking",
    description: "Everything about debates: definitions, techniques, examples and tips for fair argumentation. Learn to structure discussions and present convincingly.",
    keywords: "learn debating, AI debate, practice argumentation, fact-checking, discussion vs debate, debate techniques, argument analysis",
    url: "https://debatefy.online/"
  },
  
  factChecking: {
    title: "Real-time Fact-Checking | Debatefy",
    description: "Real-time verification through Perplexity AI ensures every argument is grounded in truth. Learn to verify claims and identify misinformation.",
    keywords: "fact checking, verify claims, truth verification, misinformation detection, perplexity AI, argument verification",
    url: "https://debatefy.online/factchecking"
  },
  
  insightScoring: {
    title: "Insight Scoring System | Debatefy",
    description: "Rewards depth of thought and new perspectives, not rhetorical victories. Learn argumentation that focuses on truth, not winning.",
    keywords: "insight scoring, argument quality, critical thinking, logical reasoning, debate scoring, argumentation skills",
    url: "https://debatefy.online/insight-scoring"
  },
  
  collaborativeGrowth: {
    title: "Collaborative Growth & Learning | Debatefy",
    description: "Split-timer system gives space for thinking without time pressure. Focus on learning and growth instead of competitive debates.",
    keywords: "collaborative debate, learning debate, chess clock timer, structured argumentation, debate practice",
    url: "https://debatefy.online/collaborative-growth"
  },
  
  setup: {
    title: "Start Your Debate | Debatefy",
    description: "Create a new debate session with AI or human opponents. Choose your thesis, set timers, and start arguing with real-time fact-checking.",
    keywords: "start debate, create debate, AI opponent, debate setup, thesis selection",
    url: "https://debatefy.online/setup"
  },
  
  chooseMode: {
    title: "Choose Your Mode | Debatefy",
    description: "Start as guest for quick access, or register to save your debates and track your progress over time.",
    keywords: "debate modes, guest mode, user registration, debate account, save debates",
    url: "https://debatefy.online/choose-mode"
  },
  
  archive: {
    title: "Debate Archive | Debatefy",
    description: "Browse and review past debates. Learn from previous arguments and see how discussions evolved.",
    keywords: "debate archive, past debates, debate history, argument review, debate analysis",
    url: "https://debatefy.online/archive"
  },
  
  about: {
    title: "About Debatefy | AI-Powered Debate Platform",
    description: "Learn about Debatefy's mission to promote fair, fact-based debates. Discover our features and how we help improve critical thinking skills.",
    keywords: "about debatefy, debate platform, AI debates, critical thinking, argumentation learning",
    url: "https://debatefy.online/about"
  }
};
